import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { ClinicsApiService } from "../../api/users/clinics-api.service";
import { Observable, of, tap, map } from "rxjs";
import {
  CreateClinicDto,
  ResponseClinicDto,
  UpdateClinicDto,
} from "../../../api/models";
import { CachedServiceBase } from "../../base/cached-service.base";

@Injectable({
  providedIn: "root",
})
export class CachedClinicsService extends CachedServiceBase<ResponseClinicDto> {
  // =============================================================================
  // DEPENDENCIES
  // =============================================================================
  private readonly clinicsApiService = inject(ClinicsApiService);

  // =============================================================================
  // CLIENT-SIDE FILTERS (live filtering)
  // =============================================================================
  /** Name filter for live search */
  public readonly nameFilter: WritableSignal<string> = signal("");

  /** Address filter for live search */
  public readonly addressFilter: WritableSignal<string> = signal("");

  // =============================================================================
  // DATA & STATE
  // =============================================================================

  // =============================================================================
  // CONSTRUCTOR & EFFECTS
  // =============================================================================
  constructor() {
    super();

    // Effect: reset pagination when filters change
    effect(() => {
      this.nameFilter();
      this.addressFilter();
      // Reset to first page when client filters change
      this.resetPagination();
    });
  }

  // =============================================================================
  // COMPUTED SIGNALS
  // =============================================================================

  /** Signal to track if there are active text filters */
  public readonly hasActiveFilters: Signal<boolean> = computed(() => {
    const name = this.nameFilter().trim();
    const address = this.addressFilter().trim();
    return !!(name || address);
  });

  /** Filtered data based on client-side filters */
  public readonly filteredData: Signal<ResponseClinicDto[]> = computed(() => {
    const allData = this.data();
    const name = this.nameFilter().toLowerCase().trim();
    const address = this.addressFilter().toLowerCase().trim();

    if (!name && !address) return allData;

    return allData.filter((item) => {
      const matchesName = !name || item.name?.toLowerCase().includes(name);
      const matchesAddress =
        !address || item.street?.toLowerCase().includes(address);

      return matchesName && matchesAddress;
    });
  });

  // =============================================================================
  // ABSTRACT METHOD IMPLEMENTATIONS
  // =============================================================================

  /**
   * Implementation of abstract method to get filtered data
   */
  protected getFilteredData(): Signal<ResponseClinicDto[]> {
    return this.filteredData;
  }

  /**
   * Refresh data from backend
   */
  public refresh(): void {
    this.clinicsApiService
      .filter$({})
      .pipe(tap((allResults) => this.updateData(allResults)))
      .subscribe();
  }

  /**
   * Reset all filters and clear data (call in OnDestroy)
   */
  public reset(): void {
    this.nameFilter.set("");
    this.addressFilter.set("");
    this.clearData();
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /**
   * Clear text filters (name and address)
   */
  public clearTextFilters(): void {
    this.nameFilter.set("");
    this.addressFilter.set("");
  }

  /**
   * Creates a new clinic
   * @param body The DTO containing clinic creation data
   * @returns Observable of the created clinic
   */
  public create$(body: CreateClinicDto): Observable<ResponseClinicDto> {
    return this.clinicsApiService.create$(body).pipe(tap(() => this.refresh()));
  }

  /**
   * Updates an existing clinic
   * @param id The id of the clinic
   * @param body The DTO containing clinic update data
   * @returns Observable of the updated clinic
   */
  public update$(
    id: string,
    body: UpdateClinicDto,
  ): Observable<ResponseClinicDto> {
    return this.clinicsApiService
      .update$(id, body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Deletes a clinic by id
   * @param id The id of the clinic
   * @returns Observable of operation result
   */
  public delete$(id: string): Observable<void> {
    return this.clinicsApiService.delete$(id).pipe(tap(() => this.refresh()));
  }

  /**
   * Retrieves a single clinic
   * @param id The id of the clinic to retrieve
   * @returns Observable of the clinic
   */
  public findOne$(id: string): Observable<ResponseClinicDto> {
    // Search in cached data
    const allData = this.data();
    const cached = allData.find((clinic) => clinic.id === id);

    if (cached) {
      return of(cached);
    }

    return this.clinicsApiService.findOne$(id);
  }

  // =============================================================================
  // LEGACY COMPATIBILITY METHODS
  // =============================================================================

  /**
   * Get all clinics (for backward compatibility with old usage)
   * @param forceRefresh Force refresh of cached data
   * @returns Observable of all clinics
   */
  public getClinics$(
    forceRefresh: boolean = false,
  ): Observable<ResponseClinicDto[]> {
    if (forceRefresh || this.data().length === 0) {
      // Return the HTTP call and wait for it to complete
      return this.clinicsApiService.filter$({}).pipe(
        tap((allResults) => this.updateData(allResults)),
        map(() => this.filteredData())
      );
    }
    return of(this.filteredData());
  }

  /**
   * Navigate to next page (legacy method - redirects to base class method)
   */
  public nextPage(): void {
    this.goToNextPage();
  }

  /**
   * Navigate to previous page (legacy method - redirects to base class method)
   */
  public previousPage(): void {
    this.goToPreviousPage();
  }

  /**
   * Gets the current page index (legacy method)
   * @returns the current page index (0-based)
   */
  public currentPageIndex(): number {
    return this.paginationInfo().currentPage;
  }

  /**
   * Legacy computed properties that map to base class
   */
  public readonly totalPages = computed(() => this.paginationInfo().totalPages);
  public readonly hasNextPage = computed(
    () => this.paginationInfo().hasNextPage,
  );
  public readonly hasPreviousPage = computed(
    () => this.paginationInfo().hasPreviousPage,
  );
  public readonly shouldShowPagination = computed(
    () => this.paginationInfo().shouldShowPagination,
  );
}
